// src/components/auth/ChangePasswordModal.js - Modal per cambio password
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, X, CheckCircle, AlertCircle } from 'lucide-react';
import ApiService from '../../services/api';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Validation rules
  const passwordRules = {
    minLength: 8,
    hasUpperCase: /[A-Z]/,
    hasLowerCase: /[a-z]/,
    hasNumber: /\d/,
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/
  };

  // Validate new password
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < passwordRules.minLength) {
      errors.push(`Almeno ${passwordRules.minLength} caratteri`);
    }
    if (!passwordRules.hasUpperCase.test(password)) {
      errors.push('Una lettera maiuscola');
    }
    if (!passwordRules.hasLowerCase.test(password)) {
      errors.push('Una lettera minuscola');
    }
    if (!passwordRules.hasNumber.test(password)) {
      errors.push('Un numero');
    }
    if (!passwordRules.hasSpecialChar.test(password)) {
      errors.push('Un carattere speciale');
    }
    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.currentPassword) {
      setError('Inserisci la password corrente');
      return;
    }
    
    if (!formData.newPassword) {
      setError('Inserisci la nuova password');
      return;
    }
    
    const passwordErrors = validatePassword(formData.newPassword);
    if (passwordErrors.length > 0) {
      setError('La nuova password deve contenere: ' + passwordErrors.join(', '));
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }
    
    if (formData.currentPassword === formData.newPassword) {
      setError('La nuova password deve essere diversa da quella corrente');
      return;
    }

    setLoading(true);
    
    try {
      await ApiService.changePassword(formData.currentPassword, formData.newPassword);
      setSuccess(true);
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      // Auto-close after success message
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Errore cambio password:', error);
      setError(error.message || 'Errore nel cambio password');
    } finally {
      setLoading(false);
    }
  };

  // Handle form reset on close
  const handleClose = () => {
    setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setError('');
    setSuccess(false);
    setShowPasswords({ current: false, new: false, confirm: false });
    onClose();
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Password strength indicator
  const getPasswordStrength = (password) => {
    const errors = validatePassword(password);
    const strength = 5 - errors.length;
    
    if (strength <= 1) return { level: 'Debole', color: 'bg-red-500', width: '20%' };
    if (strength <= 2) return { level: 'Insufficiente', color: 'bg-orange-500', width: '40%' };
    if (strength <= 3) return { level: 'Discreta', color: 'bg-yellow-500', width: '60%' };
    if (strength <= 4) return { level: 'Buona', color: 'bg-blue-500', width: '80%' };
    return { level: 'Forte', color: 'bg-green-500', width: '100%' };
  };

  const passwordStrength = formData.newPassword ? getPasswordStrength(formData.newPassword) : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <Lock className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Cambia Password</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">Password aggiornata con successo!</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800 text-sm">{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password Corrente *
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Inserisci la password corrente"
                disabled={loading || success}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                disabled={loading || success}
              >
                {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nuova Password *
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Inserisci la nuova password"
                disabled={loading || success}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                disabled={loading || success}
              >
                {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            {formData.newPassword && passwordStrength && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>Sicurezza password:</span>
                  <span className={`font-medium ${
                    passwordStrength.level === 'Forte' ? 'text-green-600' :
                    passwordStrength.level === 'Buona' ? 'text-blue-600' :
                    passwordStrength.level === 'Discreta' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {passwordStrength.level}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                    style={{ width: passwordStrength.width }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Conferma Nuova Password *
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Conferma la nuova password"
                disabled={loading || success}
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                disabled={loading || success}
              >
                {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Password Match Indicator */}
            {formData.confirmPassword && (
              <div className="mt-1 text-xs">
                {formData.newPassword === formData.confirmPassword ? (
                  <span className="text-green-600 flex items-center">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Le password coincidono
                  </span>
                ) : (
                  <span className="text-red-600 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Le password non coincidono
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Password Requirements */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs font-medium text-gray-700 mb-2">Requisiti password:</p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li className="flex items-center">
                <span className={`w-1 h-1 rounded-full mr-2 ${
                  formData.newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
                Almeno 8 caratteri
              </li>
              <li className="flex items-center">
                <span className={`w-1 h-1 rounded-full mr-2 ${
                  /[A-Z]/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
                Una lettera maiuscola
              </li>
              <li className="flex items-center">
                <span className={`w-1 h-1 rounded-full mr-2 ${
                  /[a-z]/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
                Una lettera minuscola
              </li>
              <li className="flex items-center">
                <span className={`w-1 h-1 rounded-full mr-2 ${
                  /\d/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
                Un numero
              </li>
              <li className="flex items-center">
                <span className={`w-1 h-1 rounded-full mr-2 ${
                  /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
                Un carattere speciale
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || success || 
                       !formData.currentPassword || 
                       !formData.newPassword || 
                       !formData.confirmPassword ||
                       formData.newPassword !== formData.confirmPassword ||
                       validatePassword(formData.newPassword).length > 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{loading ? 'Aggiornamento...' : 'Cambia Password'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;